const DEFAULT_TIMEOUT = 800;

function isBrowserSupported() {
  return typeof window !== 'undefined' && typeof document !== 'undefined' && typeof navigator !== 'undefined';
}

function detectBrowserName(userAgent = '') {
  const ua = userAgent.toLowerCase();
  if (ua.includes('edg/')) return 'Edge';
  if (ua.includes('chrome')) return 'Chrome';
  if (ua.includes('firefox')) return 'Firefox';
  if (ua.includes('safari') && !ua.includes('chrome')) return 'Safari';
  return 'Unknown';
}

function detectBrowserVersion(userAgent = '') {
  const match = userAgent.match(/(chrome|firefox|safari|edg|opr|version)\/?\s*(\d+(?:\.\d+)+)/i);
  return match ? match[2] : null;
}

function detectRenderingEngine(userAgent = '') {
  const ua = userAgent.toLowerCase();
  if (ua.includes('webkit')) return 'WebKit';
  if (ua.includes('gecko')) return 'Gecko';
  if (ua.includes('trident') || ua.includes('msie')) return 'Trident';
  return 'Unknown';
}

function detectDeviceType(userAgent = '', maxTouchPoints = 0) {
  const ua = userAgent.toLowerCase();
  if (ua.includes('mobile') || ua.includes('android') || maxTouchPoints > 1) return 'Mobile';
  if (ua.includes('tablet') || ua.includes('ipad')) return 'Tablet';
  return 'Desktop';
}

async function collectFingerprint() {
  if (!isBrowserSupported()) return null;

  const nav = navigator;
  const screen = window.screen || {};
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  let canvasFingerprint = null;
  let offscreenCanvasFingerprint = null;
  let webglFingerprint = null;
  let audioFingerprint = null;
  let fontFingerprint = null;
  let uaClientHints = null;
  let networkInfo = null;
  let permissions = null;
  let mediaDevices = null;
  let batteryInfo = null;
  let speechCapabilities = null;
  let apiSupport = null;
  let mediaCapabilities = null;
  let emojiFingerprint = null;

  // Enhanced Canvas Fingerprint
  if (ctx) {
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.font = '11pt no-real-font-123';
    ctx.fillText('Cwm fjordbank glyphs vext quiz, 😃', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.font = '18pt Arial';
    ctx.fillText('Cwm fjordbank glyphs vext quiz, 😃', 4, 45);
    canvasFingerprint = canvas.toDataURL().slice(0, 200);
  }

  // OffscreenCanvas Fingerprint
  try {
    if (typeof OffscreenCanvas !== 'undefined') {
      const offscreen = new OffscreenCanvas(256, 64);
      const offCtx = offscreen.getContext('2d');
      if (offCtx) {
        offCtx.font = '14px Arial';
        offCtx.fillText('OffscreenTest', 2, 15);
        const blob = await offscreen.convertToBlob();
        offscreenCanvasFingerprint = `${blob.size}-${blob.type}`;
      }
    }
  } catch (error) {
    offscreenCanvasFingerprint = null;
  }

  // Enhanced WebGL Fingerprint
  try {
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (gl) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      const extensions = gl.getSupportedExtensions() || [];
      
      webglFingerprint = {
        vendor: gl.getParameter(gl.VENDOR) || null,
        renderer: gl.getParameter(gl.RENDERER) || null,
        version: gl.getParameter(gl.VERSION) || null,
        shadingLanguageVersion: gl.getParameter(gl.SHADING_LANGUAGE_VERSION) || null,
        unmaskedVendor: debugInfo ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : null,
        unmaskedRenderer: debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : null,
        extensions: extensions.slice(0, 20), // Limit to first 20
        maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE) || null,
        maxVertexAttribs: gl.getParameter(gl.MAX_VERTEX_ATTRIBS) || null,
        maxViewportDims: gl.getParameter(gl.MAX_VIEWPORT_DIMS) || null,
        aliasedLineWidthRange: gl.getParameter(gl.ALIASED_LINE_WIDTH_RANGE) || null,
        antialiasing: gl.getContextAttributes()?.antialias || null,
      };
    }
  } catch (error) {
    webglFingerprint = null;
  }

  // Enhanced Audio Fingerprint
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (AudioCtx) {
      const audioContext = new AudioCtx();
      const oscillator = audioContext.createOscillator();
      const analyser = audioContext.createAnalyser();
      const gain = audioContext.createGain();
      const compressor = audioContext.createDynamicsCompressor();
      
      oscillator.connect(analyser);
      analyser.connect(compressor);
      compressor.connect(gain);
      gain.connect(audioContext.destination);
      
      oscillator.type = 'triangle';
      oscillator.frequency.value = 10000;
      
      audioFingerprint = {
        sampleRate: audioContext.sampleRate || null,
        state: audioContext.state || null,
        maxChannelCount: audioContext.destination?.maxChannelCount || null,
        numberOfInputs: analyser.numberOfInputs || null,
        numberOfOutputs: analyser.numberOfOutputs || null,
        channelCount: analyser.channelCount || null,
        compressorThreshold: compressor.threshold?.value || null,
        compressorKnee: compressor.knee?.value || null,
        compressorRatio: compressor.ratio?.value || null,
        compressorAttack: compressor.attack?.value || null,
        compressorRelease: compressor.release?.value || null,
      };
      
      oscillator.start(0);
      oscillator.stop(0);
      await audioContext.close();
    }
  } catch (error) {
    audioFingerprint = null;
  }

  // Enhanced Font Detection
  try {
    const baseFonts = ['monospace', 'sans-serif', 'serif'];
    const testFonts = [
      'Arial', 'Verdana', 'Times New Roman', 'Courier New', 'Georgia', 
      'Palatino', 'Garamond', 'Bookman', 'Comic Sans MS', 'Trebuchet MS',
      'Impact', 'Lucida Console', 'Tahoma', 'Helvetica', 'Apple Color Emoji',
      'Segoe UI Emoji', 'Noto Color Emoji'
    ];
    
    const testString = 'mmmmmmmmmmlli';
    const testSize = '72px';
    const canvas2 = document.createElement('canvas');
    const ctx2 = canvas2.getContext('2d');
    
    const baselines = {};
    baseFonts.forEach(baseFont => {
      ctx2.font = `${testSize} ${baseFont}`;
      baselines[baseFont] = ctx2.measureText(testString).width;
    });
    
    const detectedFonts = [];
    testFonts.forEach(font => {
      let detected = false;
      baseFonts.forEach(baseFont => {
        ctx2.font = `${testSize} '${font}', ${baseFont}`;
        const width = ctx2.measureText(testString).width;
        if (width !== baselines[baseFont]) {
          detected = true;
        }
      });
      if (detected) detectedFonts.push(font);
    });
    
    fontFingerprint = detectedFonts;
  } catch (error) {
    fontFingerprint = null;
  }

  // Emoji Rendering Test
  try {
    const emojiCanvas = document.createElement('canvas');
    const emojiCtx = emojiCanvas.getContext('2d');
    emojiCtx.font = '32px Arial';
    emojiCtx.fillText('😀🎨🔒', 0, 32);
    emojiFingerprint = emojiCanvas.toDataURL().slice(0, 100);
  } catch (error) {
    emojiFingerprint = null;
  }

  // Enhanced User-Agent Client Hints
  try {
    if (nav.userAgentData) {
      const hints = {
        brands: nav.userAgentData.brands || null,
        mobile: nav.userAgentData.mobile || null,
        platform: nav.userAgentData.platform || null,
      };
      
      // Try to get high-entropy hints
      try {
        const highEntropyValues = await nav.userAgentData.getHighEntropyValues([
          'platformVersion', 'architecture', 'bitness', 'model', 'uaFullVersion', 'fullVersionList', 'wow64'
        ]);
        Object.assign(hints, highEntropyValues);
      } catch (e) {
        // High entropy hints not available
      }
      
      uaClientHints = hints;
    }
  } catch (error) {
    uaClientHints = null;
  }

  // Network Information
  try {
    const connection = nav.connection || nav.mozConnection || nav.webkitConnection;
    networkInfo = connection ? {
      effectiveType: connection.effectiveType || null,
      rtt: connection.rtt || null,
      downlink: connection.downlink || null,
      downlinkMax: connection.downlinkMax || null,
      saveData: connection.saveData || null,
      type: connection.type || null,
    } : null;
  } catch (error) {
    networkInfo = null;
  }

  // Permissions
  try {
    if (navigator.permissions?.query) {
      const permissionNames = [
        'geolocation', 'notifications', 'camera', 'microphone', 
        'clipboard-read', 'clipboard-write', 'background-sync', 
        'persistent-storage', 'midi'
      ];
      const permissionStates = {};
      await Promise.all(permissionNames.map(async (name) => {
        try {
          const permission = await navigator.permissions.query({ name });
          permissionStates[name] = permission.state;
        } catch {
          permissionStates[name] = 'unsupported';
        }
      }));
      permissions = permissionStates;
    }
  } catch (error) {
    permissions = null;
  }

  // Media Devices Count (without exposing device names)
  try {
    if (navigator.mediaDevices?.enumerateDevices) {
      const devices = await navigator.mediaDevices.enumerateDevices();
      mediaDevices = {
        audioInput: devices.filter(d => d.kind === 'audioinput').length,
        audioOutput: devices.filter(d => d.kind === 'audiooutput').length,
        videoInput: devices.filter(d => d.kind === 'videoinput').length,
      };
    }
  } catch (error) {
    mediaDevices = null;
  }

  // Battery API
  try {
    if (navigator.getBattery) {
      const battery = await navigator.getBattery();
      batteryInfo = {
        charging: battery.charging || null,
        level: battery.level ? Math.round(battery.level * 100) : null,
      };
    }
  } catch (error) {
    batteryInfo = null;
  }

  // Speech Synthesis Voices
  try {
    if (window.speechSynthesis) {
      const voices = window.speechSynthesis.getVoices();
      speechCapabilities = {
        voiceCount: voices.length,
        languages: [...new Set(voices.map(v => v.lang))].slice(0, 10),
      };
    }
  } catch (error) {
    speechCapabilities = null;
  }

  // API Support Detection
  try {
    apiSupport = {
      webRTC: !!(nav.mediaDevices?.getUserMedia || nav.getUserMedia),
      webSocket: typeof WebSocket !== 'undefined',
      webTransport: typeof WebTransport !== 'undefined',
      webAssembly: typeof WebAssembly !== 'undefined',
      webGPU: !!navigator.gpu,
      webWorker: typeof Worker !== 'undefined',
      sharedWorker: typeof SharedWorker !== 'undefined',
      serviceWorker: 'serviceWorker' in navigator,
      broadcastChannel: typeof BroadcastChannel !== 'undefined',
      bluetooth: !!navigator.bluetooth,
      usb: !!navigator.usb,
      hid: !!navigator.hid,
      serial: !!navigator.serial,
      nfc: 'NDEFReader' in window,
      gamepad: !!navigator.getGamepads,
      xr: !!navigator.xr,
      speechRecognition: !!(window.SpeechRecognition || window.webkitSpeechRecognition),
      pdfViewer: navigator.pdfViewerEnabled || null,
      clipboard: !!navigator.clipboard,
      share: !!navigator.share,
      wakeLock: !!navigator.wakeLock,
      vibrate: !!navigator.vibrate,
    };
  } catch (error) {
    apiSupport = null;
  }

  // Media Capabilities
  try {
    if (navigator.mediaCapabilities) {
      const videoConfig = {
        type: 'file',
        video: { contentType: 'video/mp4; codecs="avc1.42E01E"', width: 1920, height: 1080, bitrate: 2000000, framerate: 30 }
      };
      const audioConfig = {
        type: 'file',
        audio: { contentType: 'audio/mp3', channels: 2, bitrate: 128000, samplerate: 44100 }
      };
      
      const [videoInfo, audioInfo] = await Promise.all([
        navigator.mediaCapabilities.decodingInfo(videoConfig).catch(() => null),
        navigator.mediaCapabilities.decodingInfo(audioConfig).catch(() => null),
      ]);
      
      mediaCapabilities = {
        video: videoInfo ? { supported: videoInfo.supported, smooth: videoInfo.smooth, powerEfficient: videoInfo.powerEfficient } : null,
        audio: audioInfo ? { supported: audioInfo.supported, smooth: audioInfo.smooth, powerEfficient: audioInfo.powerEfficient } : null,
      };
    }
  } catch (error) {
    mediaCapabilities = null;
  }

  // Collect Intl API capabilities
  const intlCapabilities = {
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
    locale: Intl.DateTimeFormat().resolvedOptions().locale || null,
    calendar: Intl.DateTimeFormat().resolvedOptions().calendar || null,
    numberingSystem: Intl.DateTimeFormat().resolvedOptions().numberingSystem || null,
    currency: Intl.NumberFormat('en', { style: 'currency', currency: 'USD' }).resolvedOptions().currency || null,
    hour12: Intl.DateTimeFormat().resolvedOptions().hour12 ?? null,
  };

  // Screen orientation
  let orientation = null;
  try {
    orientation = screen.orientation?.type || window.orientation || null;
  } catch (e) {
    orientation = null;
  }

  // Pointer capabilities
  const pointerCapabilities = {
    coarse: window.matchMedia ? window.matchMedia('(pointer: coarse)').matches : null,
    fine: window.matchMedia ? window.matchMedia('(pointer: fine)').matches : null,
    hover: window.matchMedia ? window.matchMedia('(hover: hover)').matches : null,
  };

  // Accessibility preferences
  const accessibility = {
    reducedMotion: window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : null,
    highContrast: window.matchMedia ? window.matchMedia('(prefers-contrast: high)').matches : null,
    reducedTransparency: window.matchMedia ? window.matchMedia('(prefers-reduced-transparency: reduce)').matches : null,
    colorScheme: window.matchMedia ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light') : null,
    forcedColors: window.matchMedia ? (window.matchMedia('(forced-colors: active)').matches ? 'active' : 'none') : null,
  };

  // Cache and Storage APIs
  const storageApis = {
    localStorage: typeof window.localStorage !== 'undefined',
    sessionStorage: typeof window.sessionStorage !== 'undefined',
    indexedDb: !!window.indexedDB,
    cacheStorage: !!window.caches,
  };

  const fingerprint = {
    browser: {
      name: detectBrowserName(nav.userAgent || ''),
      version: detectBrowserVersion(nav.userAgent || ''),
      engine: detectRenderingEngine(nav.userAgent || ''),
      vendor: nav.vendor || null,
    },
    os: {
      name: uaClientHints?.platform || nav.platform || null,
      version: uaClientHints?.platformVersion || null,
    },
    environment: {
      userAgent: nav.userAgent || null,
      userAgentData: uaClientHints,
      network: networkInfo,
      permissions,
      mediaDevices,
      battery: batteryInfo,
      speech: speechCapabilities,
      platform: nav.platform || null,
      deviceType: detectDeviceType(nav.userAgent || '', nav.maxTouchPoints || 0),
      screen: {
        width: screen.width || null,
        height: screen.height || null,
        availWidth: screen.availWidth || null,
        availHeight: screen.availHeight || null,
        orientation: orientation,
      },
      viewport: {
        width: window.innerWidth || null,
        height: window.innerHeight || null,
        outerWidth: window.outerWidth || null,
        outerHeight: window.outerHeight || null,
      },
      devicePixelRatio: window.devicePixelRatio || null,
      intl: intlCapabilities,
      languages: Array.isArray(nav.languages) ? Array.from(nav.languages) : [],
      locale: nav.language || null,
      cookieEnabled: nav.cookieEnabled ?? null,
      storage: storageApis,
      hardwareConcurrency: nav.hardwareConcurrency || null,
      deviceMemory: nav.deviceMemory || null,
      maxTouchPoints: nav.maxTouchPoints || null,
      colorDepth: screen.colorDepth || null,
      pixelDepth: screen.pixelDepth || null,
      touchSupport: 'ontouchstart' in window || nav.maxTouchPoints > 0,
      pointer: pointerCapabilities,
      accessibility: accessibility,
      online: navigator.onLine,
      timezoneOffset: new Date().getTimezoneOffset(),
      doNotTrack: nav.doNotTrack || null,
      globalPrivacyControl: nav.globalPrivacyControl || null,
      apiSupport: apiSupport,
      mediaCapabilities: mediaCapabilities,
    },
    canvas: canvasFingerprint ? { data: canvasFingerprint } : null,
    offscreenCanvas: offscreenCanvasFingerprint ? { data: offscreenCanvasFingerprint } : null,
    emoji: emojiFingerprint ? { data: emojiFingerprint } : null,
    webgl: webglFingerprint ? webglFingerprint : null,
    audio: audioFingerprint ? audioFingerprint : null,
    font: fontFingerprint ? { data: fontFingerprint } : null,
    gpu: nav.hardwareConcurrency ? { 
      hardwareConcurrency: nav.hardwareConcurrency,
      deviceMemory: nav.deviceMemory || null,
    } : null,
  };

  return fingerprint;
}

export { collectFingerprint, DEFAULT_TIMEOUT };
